import {
  Box,
  Button,
  Card,
  ColorInput,
  ColorSwatch,
  Group,
  Grid,
  Select,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  ActionIcon
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import {
  IconArrowBackUp,
  IconBrush,
  IconCircleCheck,
  IconColorSwatch,
  IconKey,
  IconSparkles,
  IconTypography,
  IconWand
} from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useGetColaboradorById from "@/modules/colaboradores/hooks/useGetColaboradorById";
import useUserStore from "@/modules/users/store";
import { getCurrentUser } from "@/services/auth";
import { useThemeTokens } from "@/src/theme/useThemeTokens";
import { ID_CONFIGURACOES_PERSONALIZACAO } from "@/utils/constants";
import { dataAutorizacoes } from "../empresa-page/EmpresaPage";
import { useActiveLicenciada } from "./../../modules/licenciadas/hooks/useActiveLicenciada";
import useUpdateLicenciadaTheme from "./../../modules/licenciadas/hooks/useUpdateLicenciadaTheme";
import { AVAILABLE_FONTS, DEFAULT_FONT_FAMILY, loadGoogleFont } from "./../../theme/fontLoader";
import { generatePaletteFromPrimary } from "./../../theme/paletteGenerator";

type ThemeFormValues = {
  primary: string;
  primary1: string;
  primary6: string;
  primary7: string;
  primary8: string;
  secondary: string;
  secondary4: string;
  accent: string;
  surface: string;
  surfaceMuted: string;
  surfaceSoft: string;
  textSecondary: string;
  textMuted: string;
  borderMuted: string;
  primaryAlt: string;
  textStrong: string;
  black: string;
  white: string;
  success: string;
  successAlt: string;
  warning: string;
  danger: string;
  neutral: string;
  background: string;
  premiumAqua: string;
  premiumPink: string;
  premiumOrange: string;
  tooltip: string;
  // Typography
  fontFamily: string;
  fontFamilyHeadings: string;
};

type ColorField = {
  key: Exclude<keyof ThemeFormValues, "fontFamily" | "fontFamilyHeadings">;
  label: string;
};

type ColorSection = {
  title: string;
  icon: React.ReactNode;
  color: string;
  fields: ColorField[];
};

const COLOR_SECTIONS: ColorSection[] = [
  {
    title: "Paleta principal",
    icon: <IconColorSwatch size={20} />,
    color: "blue",
    fields: [
      { key: "primary", label: "Primária base" },
      { key: "primary1", label: "Primária (ícone/tab)" },
      { key: "primary7", label: "Primária (hover)" },
      { key: "primary8", label: "Primária (ativo)" },
      { key: "primary6", label: "Primária (gradiente)" },
      { key: "primaryAlt", label: "Primária alternativa" }
    ]
  },
  {
    title: "Paleta secundária e estados",
    icon: <IconSparkles size={20} />,
    color: "teal",
    fields: [
      { key: "secondary", label: "Secundária base" },
      { key: "accent", label: "Accent/Destaque" },
      { key: "textStrong", label: "Texto forte" },
      { key: "borderMuted", label: "Borda muted" },
      { key: "tooltip", label: "Tooltip" }
    ]
  },
  {
    title: "Estados",
    icon: <IconCircleCheck size={20} />,
    color: "green",
    fields: [
      { key: "success", label: "Sucesso" },
      { key: "successAlt", label: "Sucesso (alt)" },
      { key: "warning", label: "Aviso" },
      { key: "danger", label: "Erro" },
      { key: "neutral", label: "Background" }
    ]
  }
];

const THEME_KEYS = COLOR_SECTIONS.flatMap((section) => section.fields.map((field) => field.key));

const buildThemeValues = (
  theme: TLicenciada["theme"] | undefined,
  colors: ReturnType<typeof useThemeTokens>["colors"]
): ThemeFormValues => {
  const colorValues = THEME_KEYS.reduce((accumulator, key) => {
    (accumulator as Record<string, string>)[key] = (theme as Record<string, string> | undefined)?.[key] ?? colors[key];
    return accumulator;
  }, {} as ThemeFormValues);

  return {
    ...colorValues,
    fontFamily: theme?.fontFamily ?? DEFAULT_FONT_FAMILY,
    fontFamilyHeadings: theme?.fontFamilyHeadings ?? DEFAULT_FONT_FAMILY
  };
};

const PREVIEW_COLORS: { key: keyof ThemeFormValues; label: string }[] = [
  { key: "primary", label: "Primária" },
  { key: "secondary", label: "Secundária" },
  { key: "accent", label: "Accent" },
  { key: "success", label: "Sucesso" },
  { key: "warning", label: "Aviso" },
  { key: "danger", label: "Erro" }
];

const FONT_OPTIONS = AVAILABLE_FONTS.map((font) => ({
  value: font.value,
  label: font.name
}));

const PersonalizacaoPage = () => {
  const { data: licenciada } = useActiveLicenciada();
  const { colors } = useThemeTokens();
  const colorsReference = useRef(colors);
  const lastSyncedThemeKey = useRef<string | null>(JSON.stringify(licenciada?.theme ?? {}));
  const { mutateAsync, isPending } = useUpdateLicenciadaTheme();
  const activeEmpresaId = useUserStore((store) => store.activeEmpresaId);
  const activeAutorizacao = useUserStore((store) => store.autorizacoes);
  const uid = getCurrentUser()?.uid;
  const { data: userColaborador } = useGetColaboradorById(activeEmpresaId || "", uid || "");

  const autorizacao = dataAutorizacoes.find((item) => item.label === userColaborador?.role);
  const formatAutorizacoes = activeAutorizacao?.filter((item) => item.tipo === autorizacao?.value);
  const canAccessPersonalizacao =
    userColaborador?.role === "ADMIN" ||
    formatAutorizacoes?.find((item) => item.tipoAutorizacao === ID_CONFIGURACOES_PERSONALIZACAO)?.leitura;

  const initialValues = buildThemeValues(licenciada?.theme, colors);
  const form = useForm<ThemeFormValues>({ initialValues });

  // Estado para armazenar o valor anterior de cada campo (para undo)
  const [previousValues, setPreviousValues] = useState<Partial<ThemeFormValues>>({});

  // Estado para a cor base do gerador de paleta
  const [paletteBaseColor, setPaletteBaseColor] = useState("#1F6FEB");

  useEffect(() => {
    colorsReference.current = colors;
  }, [colors]);

  useEffect(() => {
    const nextThemeKey = JSON.stringify(licenciada?.theme ?? {});
    if (nextThemeKey === lastSyncedThemeKey.current) {
      return;
    }
    lastSyncedThemeKey.current = nextThemeKey;
    const nextValues = buildThemeValues(licenciada?.theme, colorsReference.current);
    form.setValues(nextValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenciada?.theme]);

  const handleSubmit = async (values: ThemeFormValues) => {
    try {
      await mutateAsync(values);
      showNotification({ message: "Tema atualizado com sucesso!", color: "success" });
    } catch (error) {
      console.error("Erro ao salvar tema", error);
      showNotification({ message: "Erro ao salvar tema", color: "danger" });
    }
  };

  const handleColorChange = useCallback(
    (key: keyof ThemeFormValues, newValue: string) => {
      const currentValue = form.values[key];
      if (currentValue !== newValue) {
        setPreviousValues((previous) => ({ ...previous, [key]: currentValue }));
        form.setFieldValue(key, newValue);
      }
    },
    [form.setFieldValue, form.values]
  );

  const handleUndo = useCallback(
    (key: keyof ThemeFormValues) => {
      const previousValue = previousValues[key];
      if (previousValue !== undefined) {
        form.setFieldValue(key, previousValue);
        setPreviousValues((previous) => {
          const updated = { ...previous };
          delete updated[key];
          return updated;
        });
      }
    },
    [form.setFieldValue, previousValues]
  );

  const colorChangeHandlers = useMemo(() => {
    const handlers: Partial<Record<keyof ThemeFormValues, (value: string) => void>> = {};
    for (const section of COLOR_SECTIONS) {
      for (const field of section.fields) {
        handlers[field.key] = (value) => handleColorChange(field.key, value);
      }
    }
    return handlers;
  }, [handleColorChange]);

  const undoHandlers = useMemo(() => {
    const handlers: Partial<Record<keyof ThemeFormValues, () => void>> = {};
    for (const section of COLOR_SECTIONS) {
      for (const field of section.fields) {
        handlers[field.key] = () => handleUndo(field.key);
      }
    }
    return handlers;
  }, [handleUndo]);

  const handleFontFamilyChange = useCallback(
    (value: string | null) => {
      if (!value) {
        return;
      }
      loadGoogleFont(value);
      form.setFieldValue("fontFamily", value);
    },
    [form.setFieldValue]
  );

  const handleFontFamilyHeadingsChange = useCallback(
    (value: string | null) => {
      if (!value) {
        return;
      }
      loadGoogleFont(value);
      form.setFieldValue("fontFamilyHeadings", value);
    },
    [form.setFieldValue]
  );

  const handleGeneratePalette = () => {
    if (!paletteBaseColor) return;

    const generatedPalette = generatePaletteFromPrimary(paletteBaseColor);

    // Apply generated colors to form
    for (const [key, value] of Object.entries(generatedPalette)) {
      form.setFieldValue(key as keyof ThemeFormValues, value);
    }

    showNotification({
      message: "Paleta gerada! Ajuste as cores conforme necessário.",
      color: "blue"
    });
  };

  const formValues = form.values;

  if (!canAccessPersonalizacao) {
    return (
      <Card withBorder shadow="sm">
        <Group>
          <ThemeIcon size={48} radius="md" color="red" variant="light">
            <IconKey size={28} />
          </ThemeIcon>
          <Box>
            <Title order={4}>Acesso não permitido</Title>
            <Text size="sm" c="dimmed">
              Você não possui permissão para acessar a personalização de tema. Solicite ao administrador a liberação.
            </Text>
          </Box>
        </Group>
      </Card>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Card withBorder shadow="sm">
        <Group justify="space-between">
          <Group>
            <ThemeIcon size={48} radius="md" color="blue" variant="light">
              <IconBrush size={28} />
            </ThemeIcon>
            <Box>
              <Title order={3}>Personalização do tema</Title>
              <Text size="sm" c="dimmed">
                Customize as cores e tipografia do sistema para sua marca
              </Text>
            </Box>
          </Group>
          <Button type="submit" form="theme-form" loading={isPending} leftSection={<IconBrush size={16} />}>
            Aplicar tema
          </Button>
        </Group>
      </Card>

      {/* Preview de cores */}
      <Card withBorder shadow="sm">
        <Card.Section withBorder inheritPadding py="sm">
          <Text fw={500}>Preview das cores principais</Text>
        </Card.Section>
        <Card.Section inheritPadding py="md">
          <SimpleGrid cols={{ base: 3, sm: 6 }} spacing="md">
            {PREVIEW_COLORS.map(({ key, label }) => (
              <Tooltip key={key} label={`${label}: ${formValues[key]}`} withArrow>
                <Stack gap={4} align="center">
                  <ColorSwatch
                    color={formValues[key]}
                    size={40}
                    radius="md"
                    style={{ cursor: "pointer", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
                  />
                  <Text size="xs" c="dimmed" ta="center">
                    {label}
                  </Text>
                </Stack>
              </Tooltip>
            ))}
          </SimpleGrid>
        </Card.Section>
      </Card>

      {/* Formulário */}
      <form id="theme-form" onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Generate Palette Card */}
          <Card withBorder shadow="sm">
            <Stack gap="md">
              <Box>
                <Text fw={500}>Gerar paleta automática</Text>
                <Text size="sm" c="dimmed">
                  Escolha uma cor base e gere todas as cores complementares automaticamente
                </Text>
              </Box>
              <Group align="flex-end">
                <ColorInput
                  value={paletteBaseColor}
                  onChange={setPaletteBaseColor}
                  style={{ flex: 1, maxWidth: 300 }}
                />
                <Button
                  variant="filled"
                  leftSection={<IconWand size={16} />}
                  onClick={handleGeneratePalette}
                  disabled={isPending || !paletteBaseColor}
                >
                  Gerar paleta
                </Button>
              </Group>
            </Stack>
          </Card>

          <Grid>
            {COLOR_SECTIONS.map((section) => (
              <Grid.Col key={section.title} span={{ base: 12, md: 6 }}>
                <Card withBorder shadow="sm" h="100%">
                  <Card.Section withBorder inheritPadding py="sm">
                    <Group gap="sm">
                      <ThemeIcon size={32} radius="md" color={section.color} variant="light">
                        {section.icon}
                      </ThemeIcon>
                      <Text fw={500}>{section.title}</Text>
                    </Group>
                  </Card.Section>
                  <Card.Section inheritPadding py="md">
                    <Stack gap="sm">
                      {section.fields.map((field) => (
                        <Group key={field.key} gap="xs" align="flex-end" wrap="nowrap">
                          <ColorInput
                            label={field.label}
                            size="sm"
                            style={{ flex: 1 }}
                            value={formValues[field.key]}
                            onChange={colorChangeHandlers[field.key]}
                            disabled={isPending}
                          />
                          <Tooltip label="Desfazer" withArrow>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              size="lg"
                              onClick={undoHandlers[field.key]}
                              disabled={isPending || previousValues[field.key] === undefined}
                            >
                              <IconArrowBackUp size={18} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      ))}
                    </Stack>
                  </Card.Section>
                </Card>
              </Grid.Col>
            ))}
          </Grid>

          {/* Typography Section */}
          <Card withBorder shadow="sm">
            <Card.Section withBorder inheritPadding py="sm">
              <Group gap="sm">
                <ThemeIcon size={32} radius="md" color="violet" variant="light">
                  <IconTypography size={20} />
                </ThemeIcon>
                <Text fw={500}>Tipografia</Text>
              </Group>
            </Card.Section>
            <Card.Section inheritPadding py="md">
              <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Select
                    label="Fonte principal"
                    description="Fonte usada em textos e parágrafos"
                    data={FONT_OPTIONS}
                    value={formValues.fontFamily}
                    onChange={handleFontFamilyChange}
                    disabled={isPending}
                    searchable
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Select
                    label="Fonte dos títulos"
                    description="Fonte usada em cabeçalhos e títulos"
                    data={FONT_OPTIONS}
                    value={formValues.fontFamilyHeadings}
                    onChange={handleFontFamilyHeadingsChange}
                    disabled={isPending}
                    searchable
                  />
                </Grid.Col>
              </Grid>
              <Box mt="md" p="md" style={{ border: "1px dashed var(--mantine-color-gray-4)", borderRadius: 8 }}>
                <Text size="sm" c="dimmed" mb="xs">
                  Preview da tipografia:
                </Text>
                <Title order={4} style={{ fontFamily: `"${formValues.fontFamilyHeadings}", sans-serif` }}>
                  Título com a fonte selecionada
                </Title>
                <Text style={{ fontFamily: `"${formValues.fontFamily}", sans-serif` }}>
                  Este é um exemplo de texto usando a fonte principal selecionada.
                </Text>
              </Box>
            </Card.Section>
          </Card>
        </Stack>
      </form>
    </Stack>
  );
};

export default PersonalizacaoPage;
